// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@pacific/App/Components/Common/ContactTypography";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { color, mergeWithCss, xMask } from "@pacific/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
                  openInNewWindow?: boolean;
              }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps & ResolveThunks<typeof mapDispatchToProps>;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    titleClickable?: ClickablePresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        shipToInfo?: TypographyPresentationProps;
        link?: TypographyPresentationProps;
    };
    salesRepSection?: {
        wrapper?: InjectableCss;
        srRightContainer?: InjectableCss;
        image?: LazyImageProps;
        link?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        changeCustomerButton?: ButtonPresentationProps;
        signOutButton?: ButtonPresentationProps;
    };
}

const miniMyAccountStyles: MiniMyAccountStyles = {
    wrapper: {
        css: css`
            min-width: 550px;
        `,
    },
    title: {
        variant: "h5",
        css: css`
            padding: 16px;
            margin-bottom: 0;
        `,
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                padding: 0 16px;
                margin-bottom: 8px;
            `,
        },
        title: {
            variant: "h6",
            css: css`
                flex-basis: 100%;
                padding-bottom: 7px;
                border-bottom: 1px solid ${color("common.border")};
                margin-bottom: 11px;
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    margin-bottom: 12px;
                `,
            },
        },
        linkWrapper: {
            css: css`
                flex-basis: calc(50% - 8px);
            `,
        },
    },
    personalInfo: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: space-between;
                padding: 16px;
                background-color: ${color("common.ui15")};
                flex-wrap: wrap;
                gap: 16px;
            `,
        },
        infoWrapper: {
            css: css`
                flex: 1;
            `,
        },
        title: {
            variant: "h6",
            ellipsis: true,
        },
        shipToInfo: {
            size: 12,
            weight: 500,
            ellipsis: true,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                white-space: break-spaces;
            `,
        },
        link: {
            ellipsis: true,
            lineHeight: "16px",
            color: "text.link",
            size: "12px",
            weight: 600,
            css: css`
                display: flex;
                margin-bottom: 5px;
                letter-spacing: 0;
                align-items: center;
                ${IconWrapper} {
                    margin-right: 4px;
                    opacity: 0.5;
                }
            `,
        },
    },
    actionSection: {
        wrapper: {
            css: css`
                padding: 18px 16px 16px 16px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                flex-wrap: wrap;
                width: 100%;
            `,
        },
        changeCustomerButton: {
            variant: "tertiary",
            sizeVariant: "medium",
            mergeCss: true,
            css: css`
                height: 32px;
                min-height: 32px;
            `,
        },
        signOutButton: {
            sizeVariant: "medium",
            mergeCss: true,
            css: css`
                height: 34px;
                min-height: 32px;
            `,
        },
    },
    salesRepSection: {
        wrapper: {
            css: css`
                display: flex;
            `,
        },
        image: {
            width: "40px",
            height: "40px",
            css: css`
                margin-right: 8px;
                img {
                    width: 40px;
                    height: 40px;
                    object-fit: cover;
                }
            `,
            transformations: {
                width: 40,
            },
        },
        srRightContainer: {
            css: css`
                flex: 1;
            `,
        },
        link: {
            ellipsis: true,
            lineHeight: "16px",
            color: "text.link",
            size: "12px",
            weight: 600,
            css: css`
                display: flex;
                margin-bottom: 5px;
                letter-spacing: 0;
                align-items: center;
                ${IconWrapper} {
                    margin-right: 4px;
                    opacity: 0.5;
                }
            `,
        },
    },
};
const mapStateToProps = () => ({});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles, signOut, cancelPunchOut }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const history = useHistory();

    const {
        currentBillTo,
        currentShipTo,
        isPunchOutSession,
        displayChangeCustomerLink,
        changeCustomerUrl,
        salesperson,
        defaultWarehouse,
    } = useAppSelector(state => {
        const session = getSession(state);
        return {
            currentBillTo: getCurrentBillToState(state).value,
            currentShipTo: getCurrentShipToState(state).value,
            displayChangeCustomerLink: session.displayChangeCustomerLink,
            changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            isPunchOutSession: getIsPunchOutSession(state),
            firstName: session.firstName,
            lastName: session.lastName,
            defaultWarehouse: (session as any).defaultWarehouse,
            salesperson: (session as any).salesperson,
        };
    });

    const _GaMyAccountEvents = (title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "my_acct_flyout",
            click_text: title,
            destination: url,
            authentication_state: "Authenticated",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: salesPersonName || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link
                                        onClick={() => {
                                            hideAll();
                                            _GaMyAccountEvents(link?.title, link?.url);
                                        }}
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    interface SalesRepProps {
        imagePath?: string;
        name: string;
        phoneNumber?: string;
        email: string;
    }

    const salesPersonName = salesperson?.name;
    const salesPersonPhone = salesperson?.phone;
    const salesPersonEmail = salesperson?.email;
    const salesPersonImagePath = salesperson?.imagePath;
    const warehousePhone = defaultWarehouse?.phone;
    const warehouseContactName = defaultWarehouse?.contactName;
    const warehouseEmail = defaultWarehouse?.email;

    const SalesRep: FC<SalesRepProps> = ({ imagePath, name, phoneNumber, email }) => {
        return (
            <StyledWrapper {...styles.salesRepSection?.wrapper}>
                <LazyImage
                    {...styles.salesRepSection?.image}
                    src={imagePath}
                    altText={name || translate("Sales Person Picture")}
                />
                <StyledWrapper {...styles.salesRepSection?.srRightContainer}>
                    {name && email && (
                        <ContactTypography
                            type="email"
                            targetValue={email}
                            icon={true}
                            {...styles.salesRepSection?.link}
                        >
                            {name}
                        </ContactTypography>
                    )}
                    {phoneNumber && (
                        <ContactTypography
                            type="phone"
                            targetValue={phoneNumber}
                            icon={true}
                            {...styles.salesRepSection?.link}
                        >
                            {xMask.phoneNumber(phoneNumber)}
                        </ContactTypography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    };
    const signOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            {currentBillTo && (
                <Typography {...styles.title}>{`${currentBillTo?.companyName} #${
                    currentBillTo?.customerNumber
                }`}</Typography>
            )}
            {linksSection}

            <StyledWrapper {...styles.personalInfo?.wrapper}>
                {salesperson && (
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        <Typography {...styles.personalInfo?.title}>{translate("Sales Rep")}</Typography>
                        <SalesRep
                            name={salesPersonName}
                            imagePath={salesPersonImagePath}
                            phoneNumber={salesPersonPhone}
                            email={salesPersonEmail}
                        />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                    {(warehousePhone || warehouseEmail || warehouseContactName) && (
                        <>
                            <Typography {...styles.personalInfo?.title}>{translate("Your Branch Info")}</Typography>
                            {warehousePhone && (
                                <ContactTypography
                                    type="phone"
                                    targetValue={warehousePhone}
                                    icon={true}
                                    {...styles.personalInfo?.link}
                                >
                                    {xMask.phoneNumber(warehousePhone)}
                                </ContactTypography>
                            )}
                            {warehouseEmail && (
                                <ContactTypography
                                    type="email"
                                    targetValue={warehouseEmail}
                                    icon={true}
                                    {...styles.personalInfo?.link}
                                >
                                    {warehouseContactName || warehouseEmail}
                                </ContactTypography>
                            )}
                        </>
                    )}
                </StyledWrapper>
                {currentShipTo && (
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        <Typography {...styles.personalInfo?.title}>{translate("Shipping Address")}</Typography>
                        <Typography {...styles.personalInfo?.shipToInfo}>{`${currentShipTo.address1}`}</Typography>
                        <Typography {...styles.personalInfo?.shipToInfo}>
                            {`${currentShipTo.city ? `${currentShipTo.city}, ` : ""}${
                                currentShipTo.state?.abbreviation
                            } ${currentShipTo.postalCode}`}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                {displayChangeCustomerLink && (
                    <Button
                        {...styles.actionSection?.changeCustomerButton}
                        aria-label="changeCustomer"
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                            hideAll();
                        }}
                    >
                        {translate("Change Ship-To")}
                    </Button>
                )}
                <Button
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler ? onSignOutHandler() : signOutHandler();
                        hideAll();
                    }}
                    aria-label="signOut"
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniMyAccount);
